import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useConnection } from '@/app/contexts/ConnectionContext';
import type { RootState } from '@/app/store';
import { prefsStore } from './prefsStore';
import { fetchPreferences, savePreferences } from './preferencesApi';
import { clearLegacyPreferences, importLegacyPreferences } from './legacyImport';

/**
 * Loads the user's preferences for the selected connection into prefsStore.
 *
 * Children render immediately — preferences are not worth a spinner, and the
 * store serves defaults until the blob arrives. A failed load leaves the store
 * in 'failed', which disables writes so defaults cannot overwrite the server.
 *
 * The last selected connection stays in localStorage across logout, so this
 * gate must also wait for auth — otherwise /login refresh hits /preferences
 * unauthenticated and ApiService logs a CRITICAL 401.
 */
export function PreferencesGate({ children }: { children: React.ReactNode }) {
  const { selectedConnection } = useConnection();
  const connectionId = selectedConnection?.id ?? null;
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  // Bumped once the load settles. Used as a key below so the subtree remounts
  // and every useState initialiser re-reads the now-populated store.
  const [generation, setGeneration] = React.useState(0);

  useEffect(() => {
    prefsStore.reset();
    if (!connectionId || !isAuthenticated) return undefined;

    let cancelled = false;
    prefsStore.setStatus('loading');
    prefsStore.setFlusher((patch) => {
      void savePreferences(connectionId, patch).catch(() => {
        // Keep the in-memory value and retry on the next change; a failed save
        // must never interrupt whatever the user was doing.
      });
    });

    (async () => {
      try {
        const data = await fetchPreferences(connectionId);
        if (cancelled) return;
        let blob = data;
        let importedKeys: string[] = [];
        if (Object.keys(blob).length === 0) {
          const legacy = importLegacyPreferences(connectionId);
          if (Object.keys(legacy).length > 0) {
            blob = legacy;
            importedKeys = Object.keys(legacy);
          }
        }
        prefsStore.hydrate(blob);
        prefsStore.setStatus('ready');
        if (importedKeys.length > 0) {
          try {
            await savePreferences(connectionId, blob);
            if (!cancelled) clearLegacyPreferences(importedKeys);
          } catch {
            // Keep localStorage intact so the next reload can import again.
          }
        }
      } catch {
        if (cancelled) return;
        prefsStore.setStatus('failed');
      } finally {
        // Settled either way: remount so children read what is now in the store
        // (or settle on defaults once, rather than re-reading forever).
        if (!cancelled) setGeneration((g) => g + 1);
      }
    })();

    return () => {
      cancelled = true;
      prefsStore.flushNow();
      prefsStore.setFlusher(null);
    };
  }, [connectionId, isAuthenticated]);

  return <React.Fragment key={`${connectionId ?? 'none'}:${generation}`}>{children}</React.Fragment>;
}
